// https://caniuse.com/indexeddb
import Dexie, { EntityTable } from 'dexie';
import { SessionEvent } from './ISessionEvent';

const SESSION_DATA_DB_NAME = 'teachtales';
export const MAX_DELIVERY_ATTEMPTS = 3;
export const SESSION_DATA_RETENTION_PERIOD = 1000 * 60 * 60 * 24 * 30; // 30 days

export class SessionDataStore extends Dexie {
  private events!: EntityTable<SessionEvent, 'id'>;

  constructor() {
    super(SESSION_DATA_DB_NAME);
    this.version(2)
      .stores({
        events: 'id,sessionId,recordedAt,type,metadata,deliveryAttempts',
      })
      .upgrade((transaction) => {
        // No updates to existing data. New columns added.
      });
    this.version(1).stores({
      events: 'id,sessionId,recordedAt,type',
    });
    this.events.mapToClass(SessionEvent);
  }

  async getEventsBatchPendingSync(batchSize: number): Promise<SessionEvent[]> {
    return this.events.limit(batchSize).toArray();
  }

  async addEvent(event: SessionEvent): Promise<void> {
    await this.events.add(event);
  }

  async deleteSyncedEvents(events: SessionEvent[]): Promise<void> {
    await this.events.bulkDelete(events.map((e) => e.id));
  }

  async deleteOldEvents(): Promise<SessionEvent[]> {
    const oldEvents = await this.events
      .where('recordedAt')
      .below(Date.now() - SESSION_DATA_RETENTION_PERIOD)
      .toArray();
    await this.events.bulkDelete(oldEvents.map((e) => e.id));
    return oldEvents;
  }

  async processFailedEvents(events: SessionEvent[]): Promise<SessionEvent[]> {
    // Delete events that have failed to deliver X times
    const failedEvents = events.filter(
      (e) =>
        e.deliveryAttempts && e.deliveryAttempts >= MAX_DELIVERY_ATTEMPTS - 1,
    );

    await this.events.bulkDelete(failedEvents.map((e) => e.id));

    // Increment delivery attempts for remaining events
    await this.events.bulkPut(
      events
        .filter((e) => !failedEvents.includes(e))
        .map((e) => ({
          ...e,
          deliveryAttempts: (e.deliveryAttempts ?? 0) + 1,
        })),
    );

    return failedEvents;
  }
}

const sessionDB = new SessionDataStore();
export default sessionDB;
